import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Shield, 
  Zap, 
  Globe2, 
  Network, 
  Bell, 
  Lock, 
  Clock, 
  Users, 
  CheckCircle2,
  ArrowRight,
  Server,
  Activity,
  FileCode
} from 'lucide-react';

const FeaturesPage = () => {
  const { language } = useLanguage();

  const translations = {
    tr: {
      title: 'Özellikler',
      subtitle: 'Threat.live ile ağınızı koruyun',
      description: 'Gerçek zamanlı tehdit istihbaratı ve BGP tabanlı kara delik yönlendirmesi ile DDoS saldırılarına karşı proaktif koruma.',
      
      // Features
      featuresTitle: 'Neden Threat.live?',
      
      feature1Title: 'Gerçek Zamanlı Tehdit İstihbaratı',
      feature1Desc: 'Dünya genelinde honeypot ağımızdan toplanan zararlı IP\'ler anında tespit edilir ve paylaşılır.',
      
      feature2Title: 'BGP Tabanlı Koruma',
      feature2Desc: 'Standart BGP protokolü ile entegrasyon. Mevcut altyapınızla sorunsuz çalışır.',
      
      feature3Title: 'Çoklu Node Altyapısı',
      feature3Desc: '13+ ülkede 30+ aktif node ile yüksek erişilebilirlik ve güvenilirlik.',
      
      feature4Title: 'Webhook Bildirimleri',
      feature4Desc: 'Yeni tehdit IP\'leri eklendiğinde anında webhook bildirimi alın.',
      
      feature5Title: 'Kolay Entegrasyon',
      feature5Desc: 'MikroTik, Cisco, Juniper ve FortiGate vb. cihazlarla uyumlu.',
      
      feature6Title: 'Ücretsiz Kullanım',
      feature6Desc: 'Topluluk odaklı proje. Tüm özellikler tamamen ücretsiz.',

      // How it works
      howItWorksTitle: 'Nasıl Çalışır?',
      
      step1Title: 'Kayıt Olun',
      step1Desc: 'Ücretsiz hesap oluşturun ve AS numaranızı doğrulayın.',
      
      step2Title: 'BGP Oturumu Kurun',
      step2Desc: 'Router\'ınızda BGP oturumu yapılandırın.',
      
      step3Title: 'Koruma Başlasın',
      step3Desc: 'Zararlı IP\'ler otomatik olarak blackhole\'a yönlendirilir.',

      // Stats
      statsTitle: 'Rakamlarla Threat.live',
      activeNodes: 'Aktif Node',
      totalIps: 'Toplam Tehdit IP',
      countries: 'Ülke',
      uptime: 'Uptime',

      // CTA
      ctaTitle: 'Ağınızı Korumaya Başlayın',
      ctaDesc: 'Ücretsiz hesap oluşturun ve dakikalar içinde BGP tabanlı korumayı aktifleştirin.',
      ctaButton: 'Hemen Başlayın',
      learnMore: 'Kurulum Rehberi',

      // Technical
      techTitle: 'Teknik Özellikler',
      techItems: [
        'BGP Multihop desteği',
        'iBGP ve eBGP uyumlu',
        'Tüm firewall ve router\'lar ile uyumlu',
        'Webhook entegrasyonu',
        'Gerçek zamanlı BGP durumu',
        'Detaylı istatistikler',
      ],
    },
    en: {
      title: 'Features',
      subtitle: 'Protect your network with Threat.live',
      description: 'Proactive protection against DDoS attacks with real-time threat intelligence and BGP-based blackhole routing.',
      
      // Features
      featuresTitle: 'Why Threat.live?',
      
      feature1Title: 'Real-Time Threat Intelligence',
      feature1Desc: 'Malicious IPs collected from our global honeypot network are detected and shared instantly.',
      
      feature2Title: 'BGP-Based Protection',
      feature2Desc: 'Integration with standard BGP protocol. Works seamlessly with your existing infrastructure.',
      
      feature3Title: 'Multi-Node Infrastructure',
      feature3Desc: 'High availability and reliability with 30+ active nodes in 13+ countries.',
      
      feature4Title: 'Webhook Notifications',
      feature4Desc: 'Get instant webhook notifications when new threat IPs are added.',
      
      feature5Title: 'Easy Integration',
      feature5Desc: 'Compatible with MikroTik, Cisco, Juniper, FortiGate and similar devices.',
      
      feature6Title: 'Free to Use',
      feature6Desc: 'Community-driven project. All features are completely free.',

      // How it works
      howItWorksTitle: 'How It Works?',
      
      step1Title: 'Sign Up',
      step1Desc: 'Create a free account and verify your AS number.',
      
      step2Title: 'Setup BGP Session',
      step2Desc: 'Configure BGP session on your router.',
      
      step3Title: 'Protection Starts',
      step3Desc: 'Malicious IPs are automatically routed to blackhole.',

      // Stats
      statsTitle: 'Threat.live in Numbers',
      activeNodes: 'Active Nodes',
      totalIps: 'Total Threat IPs',
      countries: 'Countries',
      uptime: 'Uptime',

      // CTA
      ctaTitle: 'Start Protecting Your Network',
      ctaDesc: 'Create a free account and activate BGP-based protection in minutes.',
      ctaButton: 'Get Started',
      learnMore: 'Setup Guide',

      // Technical
      techTitle: 'Technical Specifications',
      techItems: [
        'BGP Multihop support',
        'iBGP and eBGP compatible',
        'Compatible with all firewalls and routers',
        'Webhook integration',
        'Real-time BGP status',
        'Detailed statistics',
      ],
    }
  };

  const txt = translations[language] || translations.tr;

  const features = [
    { icon: Zap, title: txt.feature1Title, desc: txt.feature1Desc, color: 'text-yellow-500' },
    { icon: Network, title: txt.feature2Title, desc: txt.feature2Desc, color: 'text-emerald-500' },
    { icon: Globe2, title: txt.feature3Title, desc: txt.feature3Desc, color: 'text-blue-500' },
    { icon: Bell, title: txt.feature4Title, desc: txt.feature4Desc, color: 'text-purple-500' },
    { icon: Server, title: txt.feature5Title, desc: txt.feature5Desc, color: 'text-cyan-500' },
    { icon: Users, title: txt.feature6Title, desc: txt.feature6Desc, color: 'text-pink-500' },
  ];

  const steps = [
    { icon: Users, title: txt.step1Title, desc: txt.step1Desc, num: '1' },
    { icon: Network, title: txt.step2Title, desc: txt.step2Desc, num: '2' },
    { icon: Shield, title: txt.step3Title, desc: txt.step3Desc, num: '3' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="features-page">
      <SEO page="features" />
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 lg:py-28">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-cyan-500/5"></div>
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                <Shield className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-500">{txt.title}</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                {txt.subtitle}
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-8">
                {txt.description}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  asChild
                  size="lg" 
                  className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white shadow-lg shadow-emerald-500/25"
                >
                  <a href="/register">
                    {txt.ctaButton}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button 
                  asChild
                  variant="outline" 
                  size="lg"
                >
                  <a href="/setup">
                    {txt.learnMore}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{txt.featuresTitle}</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-card to-muted/30 hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className={`inline-flex p-3 rounded-lg bg-background mb-4 ${feature.color}`}>
                      <feature.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{txt.howItWorksTitle}</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {steps.map((step, index) => (
                <div key={index} className="text-center relative">
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-emerald-500 to-transparent"></div>
                  )}
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border-2 border-emerald-500/30 mb-4">
                    <span className="text-3xl font-bold text-emerald-500">{step.num}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Specs */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">{txt.techTitle}</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {txt.techItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-background">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 to-cyan-600 p-8 md:p-12">
              <div className="absolute inset-0 bg-grid-white/10"></div>
              <div className="relative z-10 text-center text-white">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">{txt.ctaTitle}</h2>
                <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">{txt.ctaDesc}</p>
                <Button 
                  asChild
                  size="lg" 
                  className="bg-white text-emerald-600 hover:bg-white/90"
                >
                  <a href="/register">
                    {txt.ctaButton}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
